import React, { useState, useEffect, useRef } from "react";

interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  county?: string;
  state?: string;
  country?: string;
  postcode?: string;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  class: string;
  address?: NominatimAddress;
}

interface Result {
  display_name: string;
  lat: string;
  lon: string;
  label: string;
}

function compactLabel(addr: NominatimAddress, withPostcode: boolean): string {
  const place = addr.city || addr.town || addr.village || addr.hamlet || addr.municipality || addr.county;
  const region = addr.state || addr.country;
  if (!place || !region) return "";
  const base = `${place}, ${region}`;
  return withPostcode && addr.postcode ? `${base}, ${addr.postcode}` : base;
}

function shapeResults(raw: NominatimResult[]): Result[] {
  // Many small US cities are class "boundary" (administrative), not "place".
  // Rank "place" first, then pad with "boundary", then anything else with an address.
  const withAddr = raw.filter(r => r.address);
  const byClass = (cls: string) => withAddr.filter(r => r.class === cls);
  const pool = [...byClass("place"), ...byClass("boundary"), ...withAddr.filter(r => r.class !== "place" && r.class !== "boundary")];

  const base = pool.map(r => ({ r, label: compactLabel(r.address!, false) }))
                   .filter(x => x.label);

  const counts: Record<string, number> = {};
  base.forEach(x => { counts[x.label] = (counts[x.label] || 0) + 1; });

  return base.map(({ r, label }) => ({
    display_name: r.display_name,
    lat: r.lat,
    lon: r.lon,
    label: counts[label] > 1 ? compactLabel(r.address!, true) : label,
  }));
}

export interface GeoPoint {
  label: string;
  lat: number;
  lng: number;
}

interface Props {
  value: GeoPoint | null;
  onChange: (point: GeoPoint | null) => void;
  placeholder?: string;
}

export default function LocationSearch({ value, onChange, placeholder = "City / Township / Zip" }: Props) {
  const [query, setQuery] = useState(value?.label ?? "");
  const [results, setResults] = useState<Result[]>([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, Result[]>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!value) setQuery("");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);

    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      onChange(null);
      return;
    }

    if (q.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const key = q.trim().toLowerCase();

      const cached = cacheRef.current.get(key);
      if (cached) {
        setResults(cached);
        setOpen(cached.length > 0);
        return;
      }

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&countrycodes=us,ca&q=${encodeURIComponent(q)}`,
          { headers: { "Accept-Language": "en" }, signal: abortRef.current.signal }
        );
        const raw: NominatimResult[] = await res.json();
        const shaped = shapeResults(raw);
        cacheRef.current.set(key, shaped);
        setResults(shaped);
        setOpen(shaped.length > 0);
      } catch {
        // silently ignore network errors and aborts
      }
    }, 200);
  };

  const select = (r: Result) => {
    setQuery(r.label);
    setResults([]);
    setOpen(false);
    onChange({ label: r.label, lat: parseFloat(r.lat), lng: parseFloat(r.lon) });
  };

  const clear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
    onChange(null);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", flex: 1 }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          style={inputStyle}
        />
        {query && (
          <button onClick={clear} style={clearBtn} title="Clear">×</button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul style={dropdownStyle}>
          {results.map((r, i) => (
            <li
              key={i}
              onMouseDown={() => select(r)}
              style={itemStyle}
              onMouseEnter={e => (e.currentTarget.style.background = "#f3f4f6")}
              onMouseLeave={e => (e.currentTarget.style.background = "#fff")}
            >
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "9px 32px 9px 12px", border: "1px solid #d1d5db",
  borderRadius: 6, fontSize: 14, boxSizing: "border-box",
};

const clearBtn: React.CSSProperties = {
  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
  background: "none", border: "none", cursor: "pointer", fontSize: 18,
  color: "#9ca3af", lineHeight: 1, padding: 0,
};

const dropdownStyle: React.CSSProperties = {
  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
  background: "#fff", border: "1px solid #d1d5db", borderRadius: 6,
  margin: 0, padding: 0, listStyle: "none", zIndex: 1000,
  maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
};

const itemStyle: React.CSSProperties = {
  padding: "9px 12px", fontSize: 13, cursor: "pointer",
  borderBottom: "1px solid #f3f4f6", background: "#fff",
};
